import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Check, Clock, AlertTriangle, X, Loader2, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => n != null ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (s) => s ? new Date(s + 'T12:00:00').toLocaleDateString('fr-FR') : '—';

const STATUT = {
  paye:       { label: 'Payée',      bg: '#F0FDF4', color: '#15803D', border: '#86EFAC', Icon: Check },
  en_attente: { label: 'En attente', bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA', Icon: Clock },
  en_retard:  { label: 'En retard',  bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', Icon: AlertTriangle },
};

const EMPTY = { libelle: '', date_prevue: '', date_effective: '', montant_ttc: '' };
const GRID  = '50% 10% 10% 10% 10% 10%';

export default function EcheancesTab({ docId, totalTTC, fetchFn, createFn, updateFn, deleteFn, readOnly = false }) {
  const [echeances, setEcheances] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [newForm, setNewForm]     = useState(EMPTY);
  const [newActive, setNewActive] = useState(false);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const r = await fetchFn(docId);
      setEcheances(r.data || []);
    } catch { toast.error('Erreur chargement des échéances'); }
    finally { setLoading(false); }
  }, [docId, fetchFn]);

  useEffect(() => { load(); }, [load]);

  const totalEcheances = echeances.reduce((s, e) => s + parseFloat(e.montant_ttc || 0), 0);
  const ecart = totalTTC != null ? Math.round((totalEcheances - parseFloat(totalTTC || 0)) * 100) / 100 : null;
  const hasEcart = ecart != null && Math.abs(ecart) > 0.01;

  // ── Création via ghost row ──────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newForm.libelle.trim() || !newForm.date_prevue || !newForm.montant_ttc) {
      toast.error('Libellé, date prévue et montant TTC sont obligatoires'); return;
    }
    setSaving(true);
    try {
      await createFn(docId, {
        libelle: newForm.libelle,
        date_prevue: newForm.date_prevue,
        date_effective: newForm.date_effective || null,
        montant_ttc: parseFloat(newForm.montant_ttc),
      });
      setNewForm(EMPTY); setNewActive(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur création'); }
    finally { setSaving(false); }
  };

  // ── Édition inline ──────────────────────────────────────────────────────────
  const startEdit = (e) => {
    setEditId(e.echeance_id);
    setForm({ libelle: e.libelle || '', date_prevue: e.date_prevue || '', date_effective: e.date_effective || '', montant_ttc: e.montant_ttc != null ? String(e.montant_ttc) : '' });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await updateFn(docId, editId, { libelle: form.libelle, date_prevue: form.date_prevue, date_effective: form.date_effective || '', montant_ttc: parseFloat(form.montant_ttc) });
      setEditId(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur mise à jour'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (eid) => {
    if (!window.confirm('Supprimer cette échéance ?')) return;
    try { await deleteFn(docId, eid); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur suppression'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
    </div>
  );

  const inp = { className: "rounded border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300 w-full", style: { borderColor: 'var(--border)' } };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
      <table className="w-full text-xs" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>

        {/* Colonnes en % */}
        <colgroup>
          <col style={{ width: '50%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>

        <thead>
          <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border)' }}>
            <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Libellé</th>
            <th className="text-center px-2 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Date prévue</th>
            <th className="text-center px-2 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Date effective</th>
            <th className="text-right px-2 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Montant TTC</th>
            <th className="text-center px-2 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Statut</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {echeances.length === 0 && !newActive && (
            <tr>
              <td colSpan={6} className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>
                Aucune échéance
              </td>
            </tr>
          )}

          {echeances.map(e => {
            const st = STATUT[e.statut] || STATUT.en_attente;
            const isEditing = editId === e.echeance_id;
            return (
              <tr key={e.echeance_id}
                style={{ borderBottom: '1px solid var(--border-light)', background: isEditing ? '#F8FAFF' : 'white' }}
                onDoubleClick={() => !readOnly && !isEditing && startEdit(e)}>

                <td className="px-3 py-1.5 overflow-hidden">
                  {isEditing
                    ? <input value={form.libelle} onChange={ev => setForm(p => ({...p, libelle: ev.target.value}))} {...inp} />
                    : <span className="truncate block" style={{ color: 'var(--text)' }}>{e.libelle || '—'}</span>}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {isEditing
                    ? <input type="date" value={form.date_prevue} onChange={ev => setForm(p => ({...p, date_prevue: ev.target.value}))} {...inp} />
                    : <span style={{ color: 'var(--text-secondary)' }}>{fmtDate(e.date_prevue)}</span>}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {isEditing
                    ? <input type="date" value={form.date_effective} onChange={ev => setForm(p => ({...p, date_effective: ev.target.value}))} {...inp} />
                    : <span style={{ color: e.date_effective ? '#15803D' : 'var(--text-muted)' }}>{fmtDate(e.date_effective)}</span>}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums font-semibold" style={{ color: 'var(--text)' }}>
                  {isEditing
                    ? <input type="number" value={form.montant_ttc} onChange={ev => setForm(p => ({...p, montant_ttc: ev.target.value}))} {...inp} style={{ ...inp.style, textAlign: 'right' }} />
                    : <>{fmt(e.montant_ttc)} €</>}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                    <st.Icon size={10} />{st.label}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  {isEditing ? (
                    <div className="flex gap-1 justify-center">
                      <button onClick={handleUpdate} disabled={saving}
                        className="p-1 rounded hover:bg-green-50" style={{ color: '#15803D' }} title="Valider">
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} />}
                      </button>
                      <button onClick={() => setEditId(null)} className="p-1 rounded hover:bg-gray-50" style={{ color: '#6B7280' }} title="Annuler">
                        <X size={12} />
                      </button>
                    </div>
                  ) : !readOnly && (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(e)}
                        className="p-1 rounded hover:bg-blue-50" style={{ color: '#2563EB' }} title="Modifier">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => handleDelete(e.echeance_id)}
                        className="p-1 rounded hover:bg-red-50" style={{ color: '#DC2626' }} title="Supprimer">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}

          {/* Ghost row — nouvelle échéance */}
          {!readOnly && (
            newActive ? (
              <tr style={{ borderBottom: '1px solid var(--border-light)', background: '#F0F9FF' }}>
                <td className="px-3 py-1.5">
                  <input value={newForm.libelle} onChange={e => setNewForm(p => ({...p, libelle: e.target.value}))}
                    placeholder="Ex : Acompte 30 %" autoFocus {...inp} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="date" value={newForm.date_prevue} onChange={e => setNewForm(p => ({...p, date_prevue: e.target.value}))} {...inp} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="date" value={newForm.date_effective} onChange={e => setNewForm(p => ({...p, date_effective: e.target.value}))} {...inp} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="number" value={newForm.montant_ttc} onChange={e => setNewForm(p => ({...p, montant_ttc: e.target.value}))}
                    placeholder="0,00" {...inp} style={{ ...inp.style, textAlign: 'right' }} />
                </td>
                <td />
                <td className="px-2 py-1.5">
                  <div className="flex gap-1 justify-center">
                    <button onClick={handleCreate} disabled={saving}
                      className="p-1 rounded hover:bg-green-50" style={{ color: '#15803D' }} title="Valider">
                      {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} />}
                    </button>
                    <button onClick={() => { setNewActive(false); setNewForm(EMPTY); }}
                      className="p-1 rounded hover:bg-gray-50" style={{ color: '#6B7280' }} title="Annuler">
                      <X size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr style={{ borderBottom: '1px solid var(--border-light)' }}
                onClick={() => setNewActive(true)}
                className="cursor-pointer"
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td colSpan={6} className="px-3 py-2 text-xs" style={{ color: 'var(--text-disabled)' }}>
                  <span className="flex items-center gap-1.5">
                    <Plus size={11} /> Ajouter une échéance…
                  </span>
                </td>
              </tr>
            )
          )}
        </tbody>

        {/* Pied : total + écart */}
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--border)', background: '#F8FAFC' }}>
            <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-right" style={{ color: 'var(--text-muted)' }}>
              Total échéances
            </td>
            <td className="px-2 py-2 text-right font-bold font-mono tabular-nums text-xs"
              style={{ color: hasEcart ? '#C2410C' : 'var(--primary-solid)' }}>
              {fmt(totalEcheances)} €
            </td>
            <td colSpan={2} className="px-2 py-2 text-xs" style={{ color: hasEcart ? '#C2410C' : 'var(--text-muted)' }}>
              {ecart != null && (
                <span className="font-mono tabular-nums">
                  {hasEcart ? `Écart ${ecart > 0 ? '+' : ''}${fmt(ecart)} €` : '✓ Équilibré'}
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
